import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router"
import {
    Badge, Box, Button, Flex, Select, Spinner, Tab, TabList, TabPanel,
    TabPanels, Tabs, Td, Text, Input, useColorModeValue
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Container, Heading, Icon, IconButton, Row, Table, Tag } from "components"
import { API, Enums } from "config"
import { useAllDistributors, useDistributorMovements } from "hooks"

// ─── Hook de rastreabilidade ──────────────────────────────────────────────────

const useTraceability = (params) => useQuery({
    queryKey: ['stock-traceability', params],
    queryFn: async () => {
        if (!params.distributor_id) return null
        const p = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v)))
        const { data } = await API.get(`/stock/traceability?${p}`)
        return data
    },
    enabled: !!params.distributor_id,
})

// ─── Input de data nativo (sem conversão UTC) ─────────────────────────────────

const DateInput = ({ label, value, onChange }) => {
    const borderColor = useColorModeValue('#CBD5E0', '#4A5568')
    const bg          = useColorModeValue('white',   '#2D3748')
    const color       = useColorModeValue('#1A202C', '#E2E8F0')
    const labelC      = useColorModeValue('gray.500', 'gray.400')

    return (
        <Box>
            <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                letterSpacing="wider" color={labelC} mb={1}>{label}
            </Text>
            <input
                type="date"
                value={value}
                onChange={e => onChange(e.target.value)}
                style={{
                    height: '40px', padding: '0 12px', fontSize: '14px',
                    borderRadius: '6px', border: `1px solid ${borderColor}`,
                    background: bg, color, width: '155px', outline: 'none',
                }}
            />
        </Box>
    )
}

// ─── Busca de produto (autocomplete) ─────────────────────────────────────────

const ProductSearch = ({ value, label, onSelect }) => {
    const [term, setTerm]               = useState(label || '')
    const [debouncedTerm, setDebounced] = useState('')
    const [open, setOpen]               = useState(false)
    const wrapRef = useRef()

    useEffect(() => {
        if (!term || term === label) return
        const t = setTimeout(() => setDebounced(term), 350)
        return () => clearTimeout(t)
    }, [term, label])

    const { data: products, isFetching } = useQuery({
        queryKey: ['product-search-traceability', debouncedTerm],
        queryFn: async () => {
            const res = await API.get(`/products?term=${debouncedTerm}`)
            return Array.isArray(res.data) ? res.data : res.data?.data ?? []
        },
        enabled: debouncedTerm.length >= 2,
        staleTime: 30000,
    })

    const handleSelect = (p) => { onSelect(p); setTerm(p.name); setOpen(false) }
    const handleClear  = () => { onSelect(null); setTerm(''); setDebounced('') }

    if (value) {
        return (
            <Flex align="center" border="1.5px solid" borderColor="teal.300"
                bg="teal.50" _dark={{ bg: 'teal.900', borderColor: 'teal.500' }}
                borderRadius="md" px={3} h="40px" gap={2}
            >
                <Icon name="product" boxSize={3.5} color="teal.500" />
                <Text fontSize="xs" flex={1} noOfLines={1} fontWeight="semibold"
                    color="teal.700" _dark={{ color: 'teal.200' }}>{label || value}
                </Text>
                <IconButton icon="cancel" size="xs" variant="ghost" colorScheme="red"
                    aria-label="Remover" onClick={handleClear} />
            </Flex>
        )
    }

    return (
        <Box position="relative" ref={wrapRef}>
            <Input size="md" value={term} placeholder="Buscar produto..."
                onChange={e => { setTerm(e.target.value); setOpen(true) }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 180)}
            />
            {open && term.length >= 2 && (
                <Box position="absolute" top="calc(100% + 4px)" left={0} right={0}
                    bg="white" _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
                    border="1px solid" borderColor="gray.200"
                    borderRadius="md" boxShadow="lg" zIndex={1500} maxH="200px" overflowY="auto"
                >
                    {isFetching && <Flex justify="center" py={3}><Spinner size="sm" /></Flex>}
                    {!isFetching && !products?.length && (
                        <Text fontSize="sm" color="gray.500" px={4} py={3}>Nenhum produto encontrado</Text>
                    )}
                    {(products ?? []).slice(0, 10).map(p => (
                        <Box key={p.id} px={4} py={2} cursor="pointer"
                            _hover={{ bg: 'teal.50' }} _dark={{ _hover: { bg: 'gray.700' } }}
                            onMouseDown={() => handleSelect(p)}>
                            <Text fontSize="sm" fontWeight="semibold">{p.name}</Text>
                            {p.cod && <Text fontSize="xs" color="gray.400">Cód: {p.cod}</Text>}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}

// ─── Card de totais ───────────────────────────────────────────────────────────

const SummaryCard = ({ label, value, color, icon }) => {
    const bg     = useColorModeValue('white',    'gray.700')
    const border = useColorModeValue('gray.100', 'gray.600')
    return (
        <Box flex={1} minW="140px" bg={bg} border="1px solid" borderColor={border}
            borderRadius="lg" px={4} py={3}>
            <Flex align="center" gap={2} mb={1}>
                <Icon name={icon} boxSize={4} color={`${color}.400`} />
                <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                    letterSpacing="wider" color="gray.400">{label}
                </Text>
            </Flex>
            <Text fontSize="2xl" fontWeight="bold" color={`${color}.500`}>{value ?? 0}</Text>
        </Box>
    )
}

const EmptyState = ({ icon, text }) => (
    <Flex justify="center" align="center" direction="column" gap={2} py={10} color="gray.400">
        <Icon name={icon} boxSize={7} />
        <Text fontSize="sm">{text}</Text>
    </Flex>
)

const fmtDate = (v) => v
    ? new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    : '—'

// ─── Página principal ─────────────────────────────────────────────────────────

export default function Traceability() {
    const distributors = useAllDistributors()
    const navigate     = useNavigate()
    const warehouses   = distributors.data?.filter(d => d.is_warehouse) ?? []

    const [distributorId, setDistributorId] = useState('')
    const [productId, setProductId]         = useState('')
    const [productName, setProductName]     = useState('')
    const [from, setFrom]                   = useState('')
    const [to, setTo]                       = useState('')

    const filterBg     = useColorModeValue('gray.50',  'gray.700')
    const filterBorder = useColorModeValue('gray.100', 'gray.600')
    const labelColor   = useColorModeValue('gray.500', 'gray.400')

    const hasFilters = !!(distributorId || productId || from || to)

    const clearFilters = () => {
        setDistributorId('')
        setProductId('')
        setProductName('')
        setFrom('')
        setTo('')
    }

    const params = {
        distributor_id: distributorId,
        ...(productId && { product_id: productId }),
        ...(from      && { from }),
        ...(to        && { to }),
    }

    const { data, isFetching, refetch } = useTraceability(params)

    const summary       = data?.summary       ?? {}
    const entries       = data?.entries       ?? []
    const exitsPhysical = data?.exits_physical ?? []
    const exitsSales    = data?.exits_sales    ?? []

    const transfersIn  = useDistributorMovements(distributorId, 1, { type: Enums.StockMovementType.TRANSFER_IN,  per_page: 100 })
    const transfersOut = useDistributorMovements(distributorId, 1, { type: Enums.StockMovementType.TRANSFER_OUT, per_page: 100 })

    const transfers = [
        ...(transfersIn.data?.data  ?? []),
        ...(transfersOut.data?.data ?? []),
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

    const transfersFetching = transfersIn.isFetching || transfersOut.isFetching

    return (
        <Container maxW="90%" mt={10}>
            <Heading title="Rastreabilidade de Estoque">
                <IconButton size="sm" icon="refresh" isLoading={isFetching}
                    onClick={refetch} isDisabled={!distributorId} />
            </Heading>

            {/* Filtros */}
            <Box mt={4} bg={filterBg} border="1px solid" borderColor={filterBorder}
                borderRadius="xl" p={4} mb={5}>
                <Flex justify="space-between" align="center" mb={3}>
                    <Flex align="center" gap={2}>
                        <Icon name="search" boxSize={3.5} color={labelColor} />
                        <Text fontSize="xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="widest" color={labelColor}>
                            Filtros de Rastreabilidade
                        </Text>
                        {hasFilters && <Box w="6px" h="6px" borderRadius="full" bg="teal.400" />}
                    </Flex>
                    {hasFilters && (
                        <Button size="sm" colorScheme="red" variant="outline" onClick={clearFilters}
                            leftIcon={<Icon name="close" boxSize={3.5} />} borderRadius="full">
                            Limpar Filtros
                        </Button>
                    )}
                </Flex>
                <Flex gap={3} flexWrap="wrap" align="flex-end">
                    <Box>
                        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelColor} mb={1}>Núcleo *</Text>
                        <Select size="md" w="240px" value={distributorId}
                            onChange={e => setDistributorId(e.target.value)}>
                            <option value="">Selecione um núcleo</option>
                            {warehouses.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </Select>
                    </Box>
                    <Box w="240px">
                        <Text fontSize="2xs" fontWeight="bold" textTransform="uppercase"
                            letterSpacing="wider" color={labelColor} mb={1}>
                            Produto (opcional)
                        </Text>
                        <ProductSearch
                            value={productId} label={productName}
                            onSelect={p => { setProductId(p?.id ?? ''); setProductName(p?.name ?? '') }}
                        />
                    </Box>
                    <DateInput label="De" value={from} onChange={setFrom} />
                    <DateInput label="Até" value={to} onChange={setTo} />
                </Flex>
            </Box>

            {!distributorId ? (
                <Flex justify="center" align="center" direction="column" gap={3} py={16} color="gray.300">
                    <Icon name="movement" boxSize={10} />
                    <Text fontSize="sm" color="gray.400">
                        Selecione um núcleo para rastrear entradas e saídas de estoque.
                    </Text>
                </Flex>
            ) : isFetching ? (
                <Flex justify="center" py={16}><Spinner size="lg" /></Flex>
            ) : (
                <>
                    {/* Resumo */}
                    <Flex gap={3} mb={6} flexWrap="wrap">
                        <SummaryCard label="Entrou (NF-e)"   value={summary.total_in}           color="green" icon="check"    />
                        <SummaryCard label="Saiu por Vendas" value={summary.total_out_sales}     color="blue"  icon="order"    />
                        <SummaryCard label="Saiu Físico"     value={summary.total_out_physical}  color="red"   icon="loss"     />
                        <SummaryCard label="Saldo Rastreado" value={summary.balance}             color="teal"  icon="movement" />
                    </Flex>

                    <Tabs isLazy colorScheme="teal">
                        <TabList>
                            <Tab fontSize="sm" gap={1.5}>
                                <Icon name="check" boxSize={4} color="green.400" />
                                Entradas NF-e
                                <Badge ml={1} colorScheme="green" borderRadius="full" fontSize="xs">
                                    {entries.length}
                                </Badge>
                            </Tab>
                            <Tab fontSize="sm" gap={1.5}>
                                <Icon name="order" boxSize={4} color="blue.400" />
                                Saídas por Venda
                                <Badge ml={1} colorScheme="blue" borderRadius="full" fontSize="xs">
                                    {exitsSales.length}
                                </Badge>
                            </Tab>
                            <Tab fontSize="sm" gap={1.5}>
                                <Icon name="loss" boxSize={4} color="red.400" />
                                Saídas Físicas
                                <Badge ml={1} colorScheme="red" borderRadius="full" fontSize="xs">
                                    {exitsPhysical.length}
                                </Badge>
                            </Tab>
                            <Tab fontSize="sm" gap={1.5}>
                                <Icon name="transfer" boxSize={4} color="orange.400" />
                                Transferências
                                {transfersFetching
                                    ? <Spinner size="xs" ml={1} />
                                    : <Badge ml={1} colorScheme="orange" borderRadius="full" fontSize="xs">{transfers.length}</Badge>
                                }
                            </Tab>
                        </TabList>

                        <TabPanels>
                            {/* Entradas NF-e */}
                            <TabPanel px={0} pt={4}>
                                {entries.length === 0
                                    ? <EmptyState icon="invoice" text="Nenhuma entrada por NF-e no período." />
                                    : (
                                        <Table columns={[
                                            { children: 'Produto' },
                                            { children: 'Qtd.', isNumeric: true },
                                            { children: 'NF-e' },
                                            { children: 'Fornecedor' },
                                            { children: 'Operador' },
                                            { children: 'Data', isNumeric: true },
                                        ]}>
                                            {entries.map(e => (
                                                <Row key={e.id}>
                                                    <Td fontWeight="medium" fontSize="sm">{e.product?.name}</Td>
                                                    <Td isNumeric fontWeight="bold" color="green.500">+{e.quantity}</Td>
                                                    <Td>
                                                        {e.invoice ? (
                                                            <Box
                                                                as="button"
                                                                fontSize="xs"
                                                                fontFamily="mono"
                                                                fontWeight="semibold"
                                                                color="teal.500"
                                                                _hover={{ color: 'teal.600', textDecoration: 'underline' }}
                                                                _dark={{ _hover: { color: 'teal.300' } }}
                                                                transition="color 0.15s"
                                                                onClick={() => navigate('/stock/invoices', { state: { search: e.invoice.number } })}
                                                                title="Ver NF-e"
                                                            >
                                                                #{e.invoice.number}
                                                            </Box>
                                                        ) : (
                                                            <Text fontSize="xs" color="gray.400">—</Text>
                                                        )}
                                                    </Td>
                                                    <Td fontSize="sm">{e.invoice?.supplier_name ?? '—'}</Td>
                                                    <Td fontSize="xs">{e.employee?.name ?? '—'}</Td>
                                                    <Td isNumeric fontSize="xs" whiteSpace="nowrap">
                                                        {fmtDate(e.date)}
                                                    </Td>
                                                </Row>
                                            ))}
                                        </Table>
                                    )
                                }
                            </TabPanel>

                            {/* Saídas por venda */}
                            <TabPanel px={0} pt={4}>
                                {exitsSales.length === 0
                                    ? <EmptyState icon="order" text="Nenhuma saída por venda no período." />
                                    : (
                                        <Table columns={[
                                            { children: 'Produto' },
                                            { children: 'Lote' },
                                            { children: 'Qtd.', isNumeric: true },
                                            { children: 'Pedido' },
                                            { children: 'Status' },
                                            { children: 'Cliente' },
                                            { children: 'Consultor' },
                                            { children: 'Data', isNumeric: true },
                                        ]}>
                                            {exitsSales.map(s => (
                                                <Row key={s.id}>
                                                    <Td fontWeight="medium" fontSize="sm">{s.product?.name}</Td>
                                                    <Td>
                                                        {s.lot_number
                                                            ? <Tag colorScheme="teal" size="sm" fontFamily="mono">{s.lot_number}</Tag>
                                                            : <Text fontSize="xs" color="gray.400">Sem lote</Text>
                                                        }
                                                    </Td>
                                                    <Td isNumeric fontWeight="bold" color="blue.500">{s.quantity}</Td>
                                                    <Td>
                                                        <Text fontSize="xs" fontFamily="mono">{s.order_cod ?? '—'}</Text>
                                                    </Td>
                                                    <Td>
                                                        <Tag colorScheme="gray" size="sm">{s.order_status ?? '—'}</Tag>
                                                    </Td>
                                                    <Td fontSize="sm">{s.customer_name ?? '—'}</Td>
                                                    <Td fontSize="sm">{s.consultant_name ?? '—'}</Td>
                                                    <Td isNumeric fontSize="xs" whiteSpace="nowrap">
                                                        {fmtDate(s.date)}
                                                    </Td>
                                                </Row>
                                            ))}
                                        </Table>
                                    )
                                }
                            </TabPanel>

                            {/* Saídas físicas */}
                            <TabPanel px={0} pt={4}>
                                {exitsPhysical.length === 0
                                    ? <EmptyState icon="loss" text="Nenhuma saída física no período." />
                                    : (
                                        <Table columns={[
                                            { children: 'Tipo' },
                                            { children: 'Produto' },
                                            { children: 'Lote' },
                                            { children: 'Qtd.', isNumeric: true },
                                            { children: 'Origem / Destino' },
                                            { children: 'Operador' },
                                            { children: 'Observação' },
                                            { children: 'Data', isNumeric: true },
                                        ]}>
                                            {exitsPhysical.map(p => {
                                                const typeInfo = Enums.StockMovementType.data(p.type)
                                                return (
                                                    <Row key={p.id}>
                                                        <Td>
                                                            <Tag colorScheme={typeInfo.colorScheme} size="sm">
                                                                {p.type_label}
                                                            </Tag>
                                                        </Td>
                                                        <Td fontWeight="medium" fontSize="sm">{p.product?.name}</Td>
                                                        <Td>
                                                            {p.lot_number
                                                                ? <Tag colorScheme="teal" size="sm" fontFamily="mono">{p.lot_number}</Tag>
                                                                : <Text fontSize="xs" color="gray.400">Sem lote</Text>
                                                            }
                                                        </Td>
                                                        <Td isNumeric fontWeight="bold"
                                                            color={p.quantity > 0 ? 'green.500' : 'red.500'}>
                                                            {p.quantity > 0 ? '+' : ''}{p.quantity}
                                                        </Td>
                                                        <Td>
                                                            <Flex direction="column" gap={0.5}>
                                                                {p.origin      && <Text fontSize="xs" color="gray.500">De: {p.origin}</Text>}
                                                                {p.destination && <Text fontSize="xs" color="gray.500">Para: {p.destination}</Text>}
                                                                {!p.origin && !p.destination && <Text fontSize="xs" color="gray.400">—</Text>}
                                                            </Flex>
                                                        </Td>
                                                        <Td fontSize="xs">{p.employee?.name ?? '—'}</Td>
                                                        <Td maxW="180px">
                                                            <Text fontSize="xs" noOfLines={2} color="gray.500">
                                                                {p.notes || '—'}
                                                            </Text>
                                                        </Td>
                                                        <Td isNumeric fontSize="xs" whiteSpace="nowrap">
                                                            {fmtDate(p.date)}
                                                        </Td>
                                                    </Row>
                                                )
                                            })}
                                        </Table>
                                    )
                                }
                            </TabPanel>
                            {/* Transferências */}
                            <TabPanel px={0} pt={4}>
                                {transfersFetching ? (
                                    <Flex justify="center" py={10}><Spinner size="md" color="orange.400" /></Flex>
                                ) : transfers.length === 0 ? (
                                    <EmptyState icon="movement" text="Nenhuma transferência registrada para este núcleo." />
                                ) : (
                                    <Table columns={[
                                        { children: 'Tipo' },
                                        { children: 'Produto' },
                                        { children: 'Lote' },
                                        { children: 'Qtd.', isNumeric: true },
                                        { children: 'Origem' },
                                        { children: 'Destino' },
                                        { children: 'Operador' },
                                        { children: 'Observação' },
                                        { children: 'Data', isNumeric: true },
                                    ]}>
                                        {transfers.map(t => {
                                            const isIn = t.type === Enums.StockMovementType.TRANSFER_IN
                                            return (
                                                <Row key={t.id}>
                                                    <Td>
                                                        <Tag colorScheme={isIn ? 'blue' : 'orange'} size="sm">
                                                            {isIn ? 'Entrada' : 'Saída'}
                                                        </Tag>
                                                    </Td>
                                                    <Td fontWeight="medium" fontSize="sm">{t.product?.name}</Td>
                                                    <Td>
                                                        {t.lot_number
                                                            ? <Tag colorScheme="purple" size="sm" fontFamily="mono">{t.lot_number}</Tag>
                                                            : <Text fontSize="xs" color="gray.400">—</Text>}
                                                    </Td>
                                                    <Td isNumeric fontWeight="bold"
                                                        color={isIn ? 'blue.500' : 'orange.500'}>
                                                        {isIn ? '+' : ''}{t.quantity}
                                                    </Td>
                                                    <Td fontSize="xs">
                                                        {t.origin_distributor?.name ?? '—'}
                                                    </Td>
                                                    <Td fontSize="xs">
                                                        {t.destination_distributor?.name ?? '—'}
                                                    </Td>
                                                    <Td fontSize="xs">{t.employee?.name ?? '—'}</Td>
                                                    <Td maxW="160px">
                                                        <Text fontSize="xs" noOfLines={2} color="gray.500">
                                                            {t.notes || '—'}
                                                        </Text>
                                                    </Td>
                                                    <Td isNumeric fontSize="xs" whiteSpace="nowrap">
                                                        {fmtDate(t.created_at)}
                                                    </Td>
                                                </Row>
                                            )
                                        })}
                                    </Table>
                                )}
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </>
            )}
        </Container>
    )
}
